import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Target, Users, Search, Share2, Megaphone, Zap, TrendingUp, ShieldCheck } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia de Captação de Clientes IPTV", href: "/blog/guia-captacao-clientes-iptv" }
  ];

  const sections = [
    {
      title: "1. O Funil de Captação IPTV: Da Atração ao Teste",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Captar clientes no mercado de IPTV não é sobre "empurrar" um produto, mas sim sobre aparecer para quem já está buscando uma alternativa à TV tradicional. O processo de captação eficiente segue um funil simples: Atração (redes sociais/anúncios) → Engajamento (conversa no WhatsApp) → Conversão (teste grátis) → Fechamento (pagamento).
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O ponto crítico desse funil é o teste. No IPTV, o cliente só acredita vendo. Por isso, toda a sua estratégia de captação deve ser desenhada para levar o maior número possível de pessoas qualificadas para um teste de 2 a 4 horas.
          </p>
        </div>
      )
    },
    {
      title: "2. Canais de Captação Orgânica (Sem Gastar Dinheiro)",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Share2 className="w-6 h-6 text-electric" /> Redes Sociais Estratégicas</h4>
              <p className="text-sm text-muted-foreground">O TikTok e o Instagram Reels são as melhores ferramentas hoje. Crie vídeos mostrando os bastidores da sua configuração, a qualidade da imagem em 4K e a facilidade de uso. Use músicas virais para aumentar o alcance.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Search className="w-6 h-6 text-amber-500" /> Grupos e Comunidades</h4>
              <p className="text-sm text-muted-foreground">Não faça spam. Entre em grupos de bairros, de condomínios ou de entusiastas de tecnologia e ajude as pessoas. Quando alguém reclamar do preço da TV a cabo, apresente sua solução de forma educada e privada.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Captação Paga: Escalando com Tráfego",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Se você tem um orçamento para investir, o tráfego pago é o caminho mais rápido para encher o seu WhatsApp de leads interessados todos os dias:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Meta Ads (Instagram/Facebook)</h4>
              <p className="text-muted-foreground">Crie anúncios focados no botão "Enviar Mensagem no WhatsApp". Use criativos que foquem na economia: "Pague 10% do valor da sua fatura atual e tenha 10x mais canais".</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Google Ads (Rede de Pesquisa)</h4>
              <p className="text-muted-foreground">Apareça exatamente quando alguém digitar "melhor IPTV", "teste IPTV grátis" ou "comprar IPTV". É um público com intenção de compra altíssima.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. A Arte de Qualificar o Lead",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Nem todo mundo que entra em contato é um bom cliente. Aprenda a filtrar para não perder tempo:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Target className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Pergunta de Qualificação</h5>
                <p className="text-sm text-muted-foreground">Pergunte logo no início: "Qual dispositivo você pretende usar e qual a sua velocidade de internet?". Isso evita testes perdidos em dispositivos incompatíveis ou redes instáveis.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Zap className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Scripts de Abordagem</h5>
                <p className="text-sm text-muted-foreground">Tenha uma mensagem de boas-vindas que já explique o que é o serviço, o preço e o que o cliente ganha com o teste. Isso filtra os curiosos que só querem "coisa grátis".</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia de Captação de Clientes IPTV: Encha seu WhatsApp de Vendas"
        subtitle="As melhores estratégias orgânicas e pagas para atrair clientes qualificados todos os dias para sua revenda IPTV."
        heroImage="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Saber configurar o painel e ter um bom servidor é apenas metade do caminho. A outra metade, onde o dinheiro realmente é feito, é a captação de clientes. Sem um fluxo constante de pessoas novas conhecendo o seu serviço, sua revenda está fadada a estagnar ou morrer conforme os clientes antigos forem saindo.",
          "Este guia foi desenhado para ser o seu manual de guerra na busca por novos assinantes. Vamos explorar desde as táticas de baixo custo nas redes sociais até as estratégias avançadas de anúncios pagos que os grandes players utilizam para dominar o mercado nacional.",
          "Prepare o seu WhatsApp. Se você aplicar o que está escrito aqui, o seu celular não vai parar de apitar com novos interessados pedindo teste. Vamos às vendas."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "Vale a pena comprar listas de contatos?",
            answer: "Não. Quase sempre são listas frias, desatualizadas e que geram apenas bloqueios no WhatsApp. Prefira atrair o cliente de forma orgânica ou por anúncios, onde ele demonstra interesse primeiro."
          },
          {
            question: "Qual a melhor rede social para vender IPTV hoje?",
            answer: "O TikTok é atualmente a rede com maior entrega orgânica, permitindo que pequenos perfis alcancem milhares de pessoas sem investir em anúncios."
          }
        ]}
        benefits={[
          {
            title: "Fluxo Constante",
            description: "Nunca mais fique preocupado com a falta de clientes interessados no seu serviço.",
            icon: <TrendingUp className="w-6 h-6" />
          },
          {
            title: "Autoridade Digital",
            description: "Construa uma presença online que faz o cliente te procurar em vez de você ter que implorar pela atenção dele.",
            icon: <Megaphone className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Criação de Oferta Irresistível",
            description: "Defina o que torna sua revenda única e crie anúncios ou posts focados nesse diferencial competitivo."
          },
          {
            title: "Distribuição Estratégica",
            description: "Escolha os canais onde seu público está e comece a distribuir seu conteúdo ou seus anúncios."
          }
        ]}
        diferenciais={[
          {
            title: "Qualidade P2 PRO",
            description: "É muito mais fácil captar e converter quando você tem a certeza de que está entregando o melhor produto do mercado.",
            icon: <ShieldCheck className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="🔥 Quer Começar a Captar Clientes Hoje?"
        ctaDescription="Aproveite o suporte da P2 PRO para montar sua estratégia de vendas e utilize nosso painel de elite para converter cada teste em uma assinatura paga."
        ctaButtonText="Quero me Tornar um Revendedor de Elite"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-captacao-clientes-iptv")({
  head: () => ({
    meta: [
      { title: "Guia de Captação de Clientes IPTV: Encha seu WhatsApp de Vendas" },
      { name: "description", content: "Domine a captação de clientes para revenda IPTV. Estratégias de tráfego pago, marketing orgânico no TikTok/Instagram e técnicas de fechamento no WhatsApp." },
    ],
  }),
  component: ArticlePage,
});